<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        {{ $t('general.add_employee') }}
      </oxd-text>
      <oxd-divider />

      <oxd-form :loading="isLoading" @submit-valid="onSave">
        <div class="orangehrm-employee-container">
          <div class="orangehrm-employee-image">
            <profile-image-input
              v-model="employee.empPicture"
              :rules="rules.empPicture"
              :img-src="profilePicUrl"
            />
            <profile-id-image-input
              v-model="employee.empIDPicture"
              :rules="rules.empIDPicture"
              :img-src="profileIDPicUrl"
            />
          </div>
          <div class="orangehrm-employee-form">
            <oxd-form-row>
              <oxd-grid :cols="1" class="orangehrm-full-width-grid">
                <oxd-grid-item>
                  <full-name-input
                    v-model:firstName="employee.firstName"
                    v-model:middleName="employee.middleName"
                    v-model:lastName="employee.lastName"
                    :rules="rules"
                  />
                </oxd-grid-item>
              </oxd-grid>

              <oxd-grid :cols="2" class="orangehrm-full-width-grid">
                <oxd-grid-item>
                  <oxd-input-field
                    v-model="employee.employeeId"
                    :label="$t('general.employee_id')"
                    :rules="rules.employeeId"
                    :readonly="true"
                  />
                </oxd-grid-item>
              </oxd-grid>
            </oxd-form-row>
            <oxd-divider />
            <oxd-form-row class="user-form-header">
              <oxd-text class="user-form-header-text" tag="p">
                {{ $t('pim.create_login_details') }}
              </oxd-text>
              <oxd-switch-input v-model="createLogin" :disabled="true" />
            </oxd-form-row>

            <template v-if="createLogin">
              <oxd-form-row>
                <oxd-grid :cols="2" class="orangehrm-full-width-grid">
                  <oxd-grid-item>
                    <oxd-input-field
                      v-model="user.username"
                      :label="'電話號碼'"
                      :rules="rules.username"
                      required
                      maxlength="8"
                      autocomplete="off"
                    />
                  </oxd-grid-item>

                  <oxd-grid-item>
                    <oxd-input-field
                      v-model="user.username_match"
                      :label="'確認電話號碼'"
                      :rules="rules.username_match"
                      required
                      maxlength="8"
                      autocomplete="off"
                    />
                  </oxd-grid-item>
                </oxd-grid>
              </oxd-form-row>

              <oxd-form-row>
                <oxd-grid :cols="1" class="orangehrm-full-width-grid">
                  <oxd-grid-item class="orangehrm-location-container">
                    <div>
                      <oxd-text tag="p" class="orangehrm-input-label">
                        {{ $t('general.location') }}
                      </oxd-text>
                      <div class="orangehrm-location-checkboxes">
                        <oxd-input-field
                          v-for="location in locations"
                          :key="location.id"
                          v-model="employee.locationIds"
                          :value="location.id"
                          :label="location.label"
                          type="checkbox"
                        />
                      </div>
                    </div>
                  </oxd-grid-item>
                </oxd-grid>
              </oxd-form-row>

              <oxd-form-row>
                <oxd-grid :cols="1" class="orangehrm-full-width-grid">
                  <oxd-grid-item>
                    <oxd-input-group
                      :label="$t('general.status')"
                      :classes="{wrapper: '--status-grouped-field'}"
                    >
                      <oxd-input-field
                        v-model="user.status"
                        type="radio"
                        :option-label="$t('general.enabled')"
                        value="1"
                      />
                      <oxd-input-field
                        v-model="user.status"
                        type="radio"
                        :option-label="$t('general.disabled')"
                        value="2"
                      />
                    </oxd-input-group>
                  </oxd-grid-item>
                </oxd-grid>
              </oxd-form-row>
            </template>
          </div>
        </div>

        <oxd-divider />
        <oxd-form-actions>
          <required-text />
          <oxd-button
            display-type="ghost"
            :label="$t('general.cancel')"
            @click="onCancel"
          />
          <submit-button />
        </oxd-form-actions>
      </oxd-form>
    </div>
  </div>
</template>

<script>
import {ref, watch} from 'vue';
import {APIService} from '@/core/util/services/api.service';
import {navigate} from '@ohrm/core/util/helper/navigation';
import ProfileImageInput from '@/orangehrmPimPlugin/components/ProfileImageInput';
import ProfileIDImageInput from '@/orangehrmPimPlugin/components/ProfileIDImageInput';
import FullNameInput from '@/orangehrmPimPlugin/components/FullNameInput';
import PasswordInput from '@/core/components/inputs/PasswordInput';
import {
  maxFileSize,
  required,
  shouldNotExceedCharLength,
  shouldNotLessThanCharLength,
  validFileTypes,
} from '@ohrm/core/util/validation/rules';
import {OxdSwitchInput} from '@ohrm/oxd';
import useServerValidation from '@/core/util/composable/useServerValidation';

const defaultPic = `${window.appGlobal.publicPath}/images/default-photo.png`;
const defaultIDPic = `${window.appGlobal.publicPath}/images/default-idcard.png`;

const employeeModel = {
  firstName: '',
  middleName: '',
  lastName: '',
  empPicture: null,
  empIDPicture: null,
  employeeId: '',
  locationIds: [],
};

const userModel = {
  username: '',
  userRoleId: 2,
  empNumber: 0,
  status: '1',
  password: '',
  passwordConfirm: '',
};

export default {
  components: {
    'oxd-switch-input': OxdSwitchInput,
    'profile-image-input': ProfileImageInput,
    'profile-id-image-input': ProfileIDImageInput,
    'full-name-input': FullNameInput,
  },

  props: {
    empId: {
      type: String,
      required: true,
    },
    allowedImageTypes: {
      type: Array,
      required: true,
    },
    isPasswordRequired: {
      type: Boolean,
      default: true,
    },
    locations: {
      type: Array,
      default: () => [],
    },
    myEmpNumber: {
      type: String,
      required: true,
      default: '',
    },
  },

  setup(props) {
    const employee = ref({
      ...employeeModel,
      employeeId: props.empId ? props.empId : '',
    });
    const jobhttp = new APIService(
      window.appGlobal.baseUrl,
      `/api/v2/pim/employees/${props.myEmpNumber}/job-details`,
    );
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/pim/employees',
    );

    const {createUniqueValidator} = useServerValidation(http);
    const employeeIdUniqueValidation = createUniqueValidator(
      'Employee',
      'employeeId',
      {translateKey: 'pim.employee_id_exists'},
    );
    const usernameUniqueValidation = createUniqueValidator('User', 'userName', {
      matchByField: 'deleted',
      matchByValue: 'false',
      translateKey: 'pim.username_already_exists',
    });

    const maxSize = 1024;
    const quality = 0.9;
    let isResizing = false;

    const base64Size = (base64String) => {
      const padding = (base64String.match(/=+$/) || []).length;
      return (base64String.length * 3) / 4 - padding;
    };

    watch(
      () => employee.value.empIDPicture,
      (newValue, oldValue) => {
        if (isResizing || !newValue.base64) {
          return;
        }
        isResizing = true;

        const validImageTypes = ['image/jpeg', 'image/png'];
        if (!validImageTypes.includes(newValue.type)) {
          this.$toast.error('請選擇有效的圖像文件（JPEG 或 PNG）');
          return;
        }

        console.log(
          'Employee empIDPicture changed from',
          oldValue,
          'to',
          newValue,
        );

        const img = new Image();
        img.src = `data:${newValue.type};base64,${newValue.base64}`;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          let width = img.width;
          let height = img.height;

          // 调整图片的尺寸
          if (width > height) {
            if (width > maxSize) {
              height *= maxSize / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width *= maxSize / height;
              height = maxSize;
            }
          }

          canvas.width = width;
          canvas.height = height;

          // 在canvas上绘制图片
          ctx.drawImage(img, 0, 0, width, height);

          // 将canvas内容转换为Base64格式的图片
          const resizedImage = canvas
            .toDataURL('image/jpeg', quality)
            .split(',')[1];

          employee.value.empIDPicture.base64 = resizedImage;
          employee.value.empIDPicture.size = base64Size(resizedImage);

          isResizing = false;
        };
      },
    );

    watch(
      () => employee.value.empPicture,
      (newValue, oldValue) => {
        if (isResizing || !newValue.base64) {
          return;
        }
        isResizing = true;

        const validImageTypes = ['image/jpeg', 'image/png'];
        if (!validImageTypes.includes(newValue.type)) {
          this.$toast.error('請選擇有效的圖像文件（JPEG 或 PNG）');
          return;
        }

        console.log(
          'Employee empIDPicture changed from',
          oldValue,
          'to',
          newValue,
        );

        const img = new Image();
        img.src = `data:${newValue.type};base64,${newValue.base64}`;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          let width = img.width;
          let height = img.height;

          // 调整图片的尺寸
          if (width > height) {
            if (width > maxSize) {
              height *= maxSize / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width *= maxSize / height;
              height = maxSize;
            }
          }

          canvas.width = width;
          canvas.height = height;

          // 在canvas上绘制图片
          ctx.drawImage(img, 0, 0, width, height);

          // 将canvas内容转换为Base64格式的图片
          const resizedImage = canvas
            .toDataURL('image/jpeg', quality)
            .split(',')[1];

          employee.value.empPicture.base64 = resizedImage;
          employee.value.empPicture.size = base64Size(resizedImage);

          isResizing = false;
        };
      },
    );

    return {
      jobhttp,
      http,
      employee,
      employeeIdUniqueValidation,
      usernameUniqueValidation,
    };
  },

  data() {
    return {
      isLoading: false,
      createLogin: true,
      user: {...userModel},
      empNumber: null,
      rules: {
        firstName: [required, shouldNotExceedCharLength(30)],
        middleName: [shouldNotExceedCharLength(30)],
        lastName: [required, shouldNotExceedCharLength(30)],
        employeeId: [
          this.employeeIdUniqueValidation,
          shouldNotExceedCharLength(10),
        ],
        empPicture: [
          required,
          maxFileSize(1024 * 1024 * 20),
          validFileTypes(this.allowedImageTypes),
        ],
        empIDPicture: [
          required,
          maxFileSize(1024 * 1024 * 20),
          validFileTypes(this.allowedImageTypes),
        ],
        username: [
          required,
          this.usernameUniqueValidation,
          shouldNotLessThanCharLength(8),
          shouldNotExceedCharLength(40),
        ],
        username_match: [
          required,
          (v) => {
            return v === this.user.username || 'Username does not match';
          },
        ],
        status: [required],
      },
    };
  },

  computed: {
    profilePicUrl() {
      if (this.employee.empPicture) {
        const file = this.employee.empPicture.base64;
        const type = this.employee.empPicture.type;
        const isPicture = this.allowedImageTypes.findIndex(
          (item) => item === type,
        );
        return isPicture > -1 ? `data:${type};base64,${file}` : defaultPic;
      } else {
        return defaultPic;
      }
    },
    profileIDPicUrl() {
      if (this.employee.empIDPicture) {
        const file = this.employee.empIDPicture.base64;
        const type = this.employee.empIDPicture.type;
        const isPicture = this.allowedImageTypes.findIndex(
          (item) => item === type,
        );
        return isPicture > -1 ? `data:${type};base64,${file}` : defaultIDPic;
      } else {
        return defaultIDPic;
      }
    },
  },

  created() {
    this.isLoading = true;
    this.http.getAll().finally(() => {
      this.isLoading = false;
    });
    this.jobhttp
      .getAll()
      .then((response) => {
        this.updateJobModel(response);
      })
      .finally(() => {
        this.isLoading = false;
      });
  },

  methods: {
    onCancel() {
      navigate('/pim/viewEmployeeList');
    },
    onSave() {
      this.isLoading = true;
      this.employee.username = this.user.username;
      this.http
        .create({
          ...this.employee,
        })
        .then((response) => {
          const {data} = response;
          if (data?.data) {
            this.empNumber = data.data.empNumber;
          }
          if (this.createLogin && data?.data) {
            return this.http.request({
              method: 'POST',
              url: '/api/v2/admin/users',
              data: {
                username: this.user.username,
                password: this.user.password,
                status: this.user.status == '1',
                userRoleId: this.user.userRoleId,
                empNumber: data.data.empNumber,
              },
            });
          } else {
            return;
          }
        })
        .then(() => {
          return this.$toast.saveSuccess();
        })
        .then(() => {
          this.employee = {...employeeModel};
          this.user = {...userModel};
          if (this.empNumber) {
            navigate(`/pim/viewPersonalDetails/empNumber/${this.empNumber}`);
          } else {
            this.onCancel();
          }
        });
    },

    updateJobModel(response) {
      const {data} = response.data;
      this.employee.locationIds = [];
    },
  },
};
</script>

<style src="./employee.scss" lang="scss" scoped></style>
<style type="text/css">
.orangehrm-location-checkboxes {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
}
.orangehrm-location-checkboxes > * {
  flex: 0 0 calc(150px - 0.5rem);
  display: flex;
  flex-direction: row-reverse !important;
  align-items: start;
  justify-content: start;
}
.orangehrm-location-container {
  width: 100%;
}
</style>
